import React, { FormEvent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from '@ant-design/compatible';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);
import { Button, Input, Select, Row, Col } from 'antd';
import { isEmpty } from 'underscore';
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form';
import { RootState } from '../../../core/reducers';
import { Asset } from '../../../types';
import { bindActionCreators } from 'redux';
import * as assetActions from '../../../core/asset/assetActions';
import * as divisionActions from '../../../core/division/divisionActions';
import * as driverActions from '../../../core/driver/driverActions';

const { TIME_FORMAT_HOURS_MINS, REGEX_VALID_HOURS_MINS } = require('../../../core/constants').default;

/**
 * Submit the form to the onSubmit function after verifying there are no validation errors.
 * @param e the form event given by Form.onSubmit
 * @param form the form given by Form.create()
 * @param onSubmit api call or other final function for the form, passes param "values" representing form values as an object
 */
const handleSubmit = (
    e: FormEvent<HTMLFormElement>,
    form: WrappedFormUtils,
    onSubmit: (values: any) => {},
) => {
    e.preventDefault();
    form.validateFieldsAndScroll(['name', 'license', 'division', 'licence_type', 'asset_id', 'driver_status', 'driver_availability', 'notes', 'operation_start', 'operation_end'], (err, values) => {
        if (!err) {
            values.operation_start_time = values.operation_start;
            values.operation_end_time = values.operation_end;
            delete values.operation_start;
            delete values.operation_end;
            values.full_name = values.name;
            delete values.name;
            values.division_id = values.division;
            delete values.division;
            values.status = values.driver_status;
            delete values.driver_status;
            onSubmit(values);
        }
    });
};

interface CreateDriverFormProps {
    form: WrappedFormUtils;
    onSubmit: (values: any) => {};
    editMode?: Boolean;
}

const CreateDriverForm: React.FC<CreateDriverFormProps> = ({
    form,
    onSubmit,
    editMode = false,
}) => {
    const existingDriver = useSelector((state: RootState) => state.driver.singleDriver);
    const licenceTypeOptions = useSelector((state: RootState) => state.driver.licenceTypes);
    const driverStatusOptions = useSelector((state: RootState) => state.driver.driverStatusList);
    const divisionOptions = useSelector((state: RootState) => state.division.divisionOptions);
    const assetOptions: Asset[] = useSelector((state: RootState) => state.asset.options);
    let filteredAssetOptions = assetOptions; 
    if (!editMode && assetOptions?.length) {
        filteredAssetOptions = assetOptions.filter(a => a.driver_id === null);
    }
    const { getFieldDecorator } = form;
    const dispatch = useDispatch();
    const actionOptions: any = { 
        ...divisionActions,
        ...assetActions,
        ...driverActions,
    };
    const actions: any = bindActionCreators(actionOptions, dispatch);

    useEffect(() => {
        actions.getDivisionOptionsRequest();
        actions.getAssetOptionsRequest();
        actions.getGeneralDataRequest();
    }, [])

    return (
        <Form 
            onSubmit={(e) => handleSubmit(e, form, onSubmit)} 
            layout="vertical" 
            style={{ 
                maxWidth: '400px', 
                margin: 'auto' 
            }}
        >
            <Form.Item label="Driver name">
                {getFieldDecorator('name', {
                    initialValue: editMode && existingDriver?.profile?.full_name || '',
                    rules: [
                        {
                            required: true,
                            message: 'Please input the driver\'s name',
                        },
                    ],
                })(
                    <Input 
                        placeholder="Enter Driver Name" 
                        data-testid='create-edit-driver_name-input'
                    />
                )}
            </Form.Item>
            <Form.Item label="Driver license">
                {getFieldDecorator('license', { 
                    initialValue: editMode && existingDriver?.profile?.licence_number || '',
                })(
                    <Input 
                        placeholder="Enter Driver license" 
                        data-testid='create-edit-driver_licence-input'
                    />
                )}
            </Form.Item>
            <Form.Item label="Division">
                {getFieldDecorator('division', { 
                    initialValue: divisionOptions?.length && editMode && existingDriver?.division?.id || null,
                    rules: [
                        {
                            required: !editMode,
                            message: 'Please select a division',
                        }
                    ],
                })(
                    <Select
                        placeholder="Select Division"
                        showSearch
                        filterOption={(txt, obj) => (obj?.props?.children?.toLowerCase().includes(txt.toLowerCase()))}
                        data-testid='create-edit-driver_division-select'
                    >
                        {!isEmpty(divisionOptions) && divisionOptions.map((item, i) => (
                            <Select.Option 
                                key={`division-${i}`}
                                value={item.id}
                            >
                                {item.name}
                            </Select.Option>
                        ))}
                    </Select>
                )}
            </Form.Item>
            <Form.Item label="Asset">
                {getFieldDecorator('asset_id')(
                    <Select
                        filterOption={(input, option) => option?.props?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                        placeholder="Select Asset"
                        showSearch
                        data-testid='create-edit-driver_asset-select'
                    >
                        {filteredAssetOptions?.map((a, i) => (
                            <Select.Option
                                key={`asset-${i}`}
                                value={a.id}
                            >
                                {a.name}
                            </Select.Option>
                        ))}
                    </Select>
                )}
            </Form.Item>
            <Form.Item label="Licence type">
                {getFieldDecorator('licence_type', { 
                    initialValue: licenceTypeOptions?.length && editMode && existingDriver?.licenceType?.id || null
                })(
                    <Select
                        placeholder="Select License Type"
                        data-testid='create-edit-driver_licence-type-select'
                    >
                        {!isEmpty(licenceTypeOptions) && licenceTypeOptions.map((item, i) => (
                            <Select.Option 
                                key={`licence-type-${i}`}
                                value={item.id}
                            >
                                {item.title}
                            </Select.Option>
                        ))}
                    </Select>
                )}
            </Form.Item>
            <Form.Item label="Driver status">
                {getFieldDecorator('driver_status', { 
                    initialValue: editMode && existingDriver?.profile?.driver_status || null,
                })(
                    <Select
                        placeholder="Select Status"
                        data-testid='create-edit-driver_status-select'
                    >
                        {!isEmpty(driverStatusOptions) && driverStatusOptions.map((item) => (
                            <Select.Option 
                                key={item.key} 
                                value={(item.key).toString()}
                            >
                                {item.title}
                            </Select.Option>
                        ))}
                    </Select>
                )}
            </Form.Item>
            <Form.Item label="Driver availability">
                {getFieldDecorator('driver_availability', { 
                    initialValue: editMode && existingDriver?.profile?.driver_availability 
                        ? parseInt(existingDriver.profile.driver_availability, 10) 
                        : null,
                })(
                    <Select
                        placeholder="Select Availability"
                        data-testid='create-edit-driver_availability-select'
                    >
                        <Select.Option value={1}>Available</Select.Option>
                        <Select.Option value={0}>Not Available</Select.Option>
                    </Select>
                )}
            </Form.Item>
            <Row>
                <Col span={11}>                    
                    <Form.Item label="Available from">
                        {getFieldDecorator('operation_start', { 
                            initialValue: editMode && !isEmpty(existingDriver?.profile?.operation_start_time) 
                                ? dayjs(existingDriver.profile.operation_start_time, TIME_FORMAT_HOURS_MINS).format('HH:MM') 
                                : dayjs().format('HH:MM') ,
                            rules: [{
                                pattern: REGEX_VALID_HOURS_MINS,
                                message: 'Must be in HH:MM format!',
                            }]
                        })(
                            <Input
                                type='text'
                                placeholder="HH:MM"
                                data-testid='create-edit-driver_available-from-input'
                            />
                        )}
                    </Form.Item>
                </Col>
                <Col span={2} />
                <Col span={11}>
                    <Form.Item label="Available To">
                        {getFieldDecorator('operation_end', { 
                            initialValue: editMode && !isEmpty(existingDriver?.profile?.operation_end_time) 
                                ? dayjs(existingDriver.profile.operation_end_time, TIME_FORMAT_HOURS_MINS).format('HH:MM')   
                                : dayjs().format('HH:MM'),
                            rules: [{
                                pattern: REGEX_VALID_HOURS_MINS,
                                message: 'Must be in HH:MM format!',
                            }]
                        })(
                            <Input
                                type='text'
                                placeholder="HH:MM"
                                data-testid='create-edit-driver_available-to-input'
                            />
                        )}
                    </Form.Item>
                </Col>
            </Row>
            <Form.Item label="Notes">
                {getFieldDecorator('notes', { 
                    initialValue: editMode && existingDriver?.profile?.notes || '',
                })(
                    <Input.TextArea 
                        placeholder="Enter Notes" 
                        style={{ height: 100 }} 
                        data-testid='create-edit-driver_notes-input'
                    />
                )}
            </Form.Item>
            <div className="mt-2 text-center">
                <Button 
                    htmlType="submit" 
                    type="primary"
                    data-testid='create-edit-driver_save-btn'
                >
                    {editMode ? 'Update' : 'Create'}
                </Button>
            </div>
        </Form>
    );
}

const WrappedCreateDriverForm = Form.create({ name: 'driver-create-form' })(CreateDriverForm);

export default WrappedCreateDriverForm;
